import type { Loop } from '@/common/api/types/loops';
import type { ObjectiveHealth } from '@/common/api/types/objectives';
import type { PersonaView } from '@/features/agentic-insights/components/data/resolvePersonaView';
import type { Finding } from '@/features/agentic-insights/components/data/types';
import { RunProse } from '@/features/agentic-insights/components/experimental/shared/prose/RunProse';
import { MetaLine } from '@/features/agentic-insights/components/experimental/shared/ui/controls';
import { CONFIDENCE_TONE, MEASURE, TYPE } from '../language';
import { useFindingMeta } from './findingMeta';
import { LoopStamp } from './LoopStamp';

interface FindingHeadProps {
  finding: Finding;
  view: Extract<PersonaView, { kind: 'voiced' }>;
  /** Keyed by objective id, for the meta line's objective-scope entry. Undefined while loading. */
  objectiveHealthById: Record<number, ObjectiveHealth> | undefined;
  isObjectiveHealthPending: boolean;
  /** Present only in the All loops reading — see `LoopStamp`. */
  loop?: Loop;
  onSwitchLoop?: (loopId: number) => void;
}

/**
 * The claim and its standing — used only where the finding is not already named on screen, which is
 * the Details reading, where the rail carries truncated claims and the pane needs a title.
 *
 * The list reading deliberately does not use it: the row a reader just opened is the title, and
 * repeating it under itself makes an expansion that restates rather than adds.
 *
 * Uses this variant's own `findingMeta` (the one `FindingRow` also uses), not the shared one — the
 * shared version has no notion of objective health, so an objective-scoped finding read in Details
 * mode was losing the progress bar and health word list mode already shows, and falling silently
 * back to a titleless label for an objective the nav loop's own list doesn't carry.
 */
export const FindingHead = ({
  finding,
  view,
  objectiveHealthById,
  isObjectiveHealthPending,
  loop,
  onSwitchLoop,
}: FindingHeadProps) => {
  const baseMeta = useFindingMeta(
    finding,
    CONFIDENCE_TONE,
    objectiveHealthById,
    isObjectiveHealthPending,
  );
  // Confidence first, then the loop (All loops only), then whatever else the finding carries.
  const meta =
    loop && onSwitchLoop
      ? [
          baseMeta[0],
          <LoopStamp key="loop" loop={loop} onSwitch={onSwitchLoop} />,
          ...baseMeta.slice(1),
        ]
      : baseMeta;

  return (
    <div className="flex flex-col gap-3">
      <h2 className={`${TYPE.claimLead} ${MEASURE}`}>
        <RunProse>{view.businessStatement}</RunProse>
      </h2>
      <MetaLine type={TYPE} items={meta} />
    </div>
  );
};
